import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Phone, Fuel, Shield, Users, Gauge, Download, FileText, ChevronDown, ChevronUp, Calculator } from 'lucide-react';
import { carModels, advisorInfo } from '../data/mock';
import { Slider } from './ui/slider';

const HighlightIcon = ({ type }) => {
  switch (type) {
    case 'engine':
      return <Gauge className="w-4 h-4 md:w-5 md:h-5" />;
    case 'fuel':
      return <Fuel className="w-4 h-4 md:w-5 md:h-5" />;
    case 'safety':
      return <Shield className="w-4 h-4 md:w-5 md:h-5" />;
    case 'seats':
      return <Users className="w-4 h-4 md:w-5 md:h-5" />;
    default:
      return <FileText className="w-4 h-4 md:w-5 md:h-5" />;
  }
};

// Generate highlights from specs
const getHighlights = (model) => {
  const specs = model.specs;
  if (!specs) {
    return [
      { icon: 'engine', label: model.category, detail: '2026' },
      { icon: 'fuel', label: 'ECO', detail: 'Fuel Efficient' },
      { icon: 'safety', label: '5-Star', detail: 'ASEAN NCAP' },
      { icon: 'seats', label: '5-Seater', detail: 'Comfortable' }
    ];
  }
  
  return [
    { 
      icon: 'engine', 
      label: specs.displacement || specs.engine?.split(',')[0] || model.category,
      detail: specs.power?.split('/')[0] || 'DOHC'
    },
    { 
      icon: 'fuel', 
      label: specs.fuelConsumption?.split(' ')[0] || 'ECO',
      detail: 'Fuel Economy'
    },
    { 
      icon: 'safety', 
      label: specs.ncap?.includes('5-Star') ? '5-Star' : '4-Star',
      detail: 'ASEAN NCAP'
    },
    { 
      icon: 'seats', 
      label: specs.seating || (specs.groundClearance ? specs.groundClearance : '5-Seater'),
      detail: specs.seating ? 'Family' : (specs.groundClearance ? 'Ground' : 'Comfortable')
    }
  ];
};

const formatPrice = (price) => {
  return `RM${price.toLocaleString()}`;
};

// Calculate monthly payment
const calculateMonthly = (price, depositPercent, years, interestRate) => {
  const deposit = price * (depositPercent / 100);
  const loanAmount = price - deposit;
  const months = years * 12;
  const monthlyRate = interestRate / 100 / 12;
  
  if (monthlyRate === 0) {
    return Math.round(loanAmount / months);
  }
  
  const monthly = (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
  return Math.round(monthly);
};

const ModelCard = ({ model }) => {
  const highlights = getHighlights(model);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [showCalculator, setShowCalculator] = useState(false);
  const [depositPercent, setDepositPercent] = useState(0);
  const [loanYears, setLoanYears] = useState(9);

  const selectedVariant = model.variants[selectedVariantIndex];
  const price = selectedVariant?.price || 0;
  const monthly = calculateMonthly(price, depositPercent, loanYears, model.interestRate);
  const deposit = price * (depositPercent / 100);
  const loanAmount = price - deposit;

  const handleWhatsApp = () => {
    const message = `[Website] PM PERODUA - Hi Syasyah, saya berminat dengan ${model.name} ${selectedVariant.name}. Harga ${formatPrice(price)}, deposit ${depositPercent}%, ansuran ±${formatPrice(monthly)}/bulan (${loanYears} tahun). Boleh proceed?`;
    window.open(`${advisorInfo.whatsappLink}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleBrochure = () => {
    window.open(model.brochureLink, '_blank');
  };

  return (
    <Card className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-0 bg-white rounded-2xl">
      <CardContent className="p-0">
        {/* Image Section */}
        <div className="relative bg-gradient-to-br from-slate-100 to-slate-200 p-4 md:p-6 h-40 md:h-52 flex items-center justify-center">
          {model.isNew && (
            <div className="absolute top-3 left-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-1 rounded-full text-[10px] md:text-xs font-bold animate-pulse z-10">
              🔥 NEW MODEL
            </div>
          )}
          {model.rebate && (
            <div className="absolute top-3 right-3 bg-gradient-to-r from-orange-500 to-red-500 text-white px-2 py-1 rounded-full text-[10px] md:text-xs font-bold animate-pulse z-10">
              🎁 RM{(model.rebate.amount/1000).toFixed(0)}K
            </div>
          )}
          {!model.rebate && (
            <div className="absolute top-3 right-3 bg-white/90 backdrop-blur px-2 py-1 rounded-lg text-[10px] md:text-xs font-semibold text-slate-700">
              {model.category}
            </div>
          )}
          <img
            src={model.image}
            alt={model.name}
            className="max-h-28 md:max-h-40 w-auto object-contain group-hover:scale-110 transition-transform duration-300"
          />
        </div>

        {/* Content Section */}
        <div className="p-4 md:p-5">
          {/* Title */}
          <div className="mb-3">
            <h3 className="text-lg md:text-xl font-bold text-slate-900">
              {model.name}
            </h3>
            <p className="text-xs md:text-sm text-slate-500 line-clamp-1">{model.description}</p>
          </div>

          {/* Highlights Grid */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            {highlights.map((highlight, idx) => (
              <div
                key={idx}
                className="flex items-center space-x-2 bg-slate-50 rounded-lg p-2"
              >
                <div className="text-blue-600">
                  <HighlightIcon type={highlight.icon} />
                </div>
                <div>
                  <div className="text-[10px] md:text-xs font-semibold text-slate-800">{highlight.label}</div>
                  <div className="text-[9px] md:text-xs text-slate-500">{highlight.detail}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Clickable Variant Badges */}
          <div className="mb-3">
            <p className="text-[10px] md:text-xs text-slate-500 mb-1.5">Pilih Variant:</p>
            <div className="flex flex-wrap gap-1.5">
              {model.variants.map((variant, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedVariantIndex(idx)}
                  className={`px-2.5 py-1 rounded-full text-[10px] md:text-xs font-medium transition-all duration-200 ${
                    selectedVariantIndex === idx
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                >
                  {variant.name}
                </button>
              ))}
            </div>
          </div>

          {/* Waiting List */}
          <div className="mb-3">
            <span className="text-[10px] md:text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
              {model.waitingList}
            </span>
          </div>

          {/* Price & Monthly */}
          <div className="flex items-center justify-between mb-3 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
            <div>
              <p className="text-[10px] md:text-xs text-slate-500">Harga OTR</p>
              <p className="text-lg md:text-xl font-bold text-blue-600">{formatPrice(price)}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] md:text-xs text-slate-500">Ansuran/bulan</p>
              <p className="text-base md:text-lg font-bold text-green-600">±{formatPrice(monthly)}</p>
              <p className="text-[9px] text-slate-400">{loanYears} tahun, {depositPercent}% deposit</p>
            </div>
          </div>

          {/* Expandable Loan Calculator */}
          <div className="mb-3">
            <button
              onClick={() => setShowCalculator(!showCalculator)}
              className="w-full flex items-center justify-center gap-2 py-2 px-3 bg-slate-100 hover:bg-slate-200 rounded-xl text-xs md:text-sm font-medium text-slate-700 transition-all duration-200"
            >
              <Calculator className="w-4 h-4" />
              <span>Kira Loan Anda</span>
              {showCalculator ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {showCalculator && (
              <div className="mt-3 p-4 bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl border border-slate-200 space-y-4 animate-in slide-in-from-top-2 duration-200">
                {/* Deposit Slider */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-xs font-medium text-slate-700">Deposit</label>
                    <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                      {depositPercent}% ({formatPrice(deposit)})
                    </span>
                  </div>
                  <Slider
                    value={[depositPercent]}
                    onValueChange={(value) => setDepositPercent(value[0])}
                    max={30}
                    min={0}
                    step={5}
                    className="w-full"
                  />
                  <div className="flex justify-between text-[9px] text-slate-400 mt-1">
                    <span>0%</span>
                    <span>10%</span>
                    <span>20%</span>
                    <span>30%</span>
                  </div>
                </div>

                {/* Loan Tenure */}
                <div>
                  <label className="text-xs font-medium text-slate-700 block mb-2">Tempoh Loan</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[5, 7, 9].map((years) => (
                      <button
                        key={years}
                        onClick={() => setLoanYears(years)}
                        className={`py-2 rounded-lg text-xs font-medium transition-all ${
                          loanYears === years
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'bg-white text-slate-700 border border-slate-200 hover:border-blue-300'
                        }`}
                      >
                        {years} Tahun
                      </button>
                    ))}
                  </div>
                </div>

                {/* Calculation Breakdown */}
                <div className="bg-white rounded-lg p-3 space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Harga OTR</span>
                    <span className="font-medium">{formatPrice(price)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Deposit ({depositPercent}%)</span>
                    <span className="font-medium text-orange-600">- {formatPrice(deposit)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Jumlah Loan</span>
                    <span className="font-medium">{formatPrice(loanAmount)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Interest Rate</span>
                    <span className="font-medium">{model.interestRate}% p.a.</span>
                  </div>
                  <div className="border-t border-slate-200 pt-2 mt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700">Ansuran Bulanan</span>
                      <span className="text-lg font-bold text-green-600">±{formatPrice(monthly)}</span>
                    </div>
                  </div>
                </div>

                {/* Interest Rate Note */}
                <p className="text-[9px] text-slate-400 text-center">
                  *Kadar faedah bergantung kepada profil peminjam. Hubungi untuk kadar sebenar.
                </p>
              </div>
            )}
          </div>

          {/* CTA Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={handleWhatsApp}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 md:py-3 rounded-xl text-xs md:text-sm transition-all duration-300"
            >
              <Phone className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5" />
              WhatsApp
            </Button>
            <Button
              onClick={handleBrochure}
              variant="outline"
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white font-semibold py-2.5 md:py-3 px-3 md:px-4 rounded-xl text-xs md:text-sm transition-all duration-300"
            >
              <Download className="w-3.5 h-3.5 md:w-4 md:h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ModelsSection = () => {
  return (
    <section id="models" className="py-16 md:py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-12">
          <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <span>🚗</span>
            <span>Latest Models 2026</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Senarai Model & <span className="text-blue-600">Harga</span>
          </h2>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
            Pilih dari koleksi terkini Perodua dengan harga terbaik dan ansuran yang berpatutan
          </p>
        </div>

        {/* Category Filter Badges */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-8">
          <span className="bg-white shadow-sm px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium text-slate-700 border border-slate-200">
            📁 7 Model Tersedia
          </span>
          <span className="bg-green-100 text-green-700 px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium">
            ✅ Harga OTR 2026
          </span>
          <span className="bg-purple-100 text-purple-700 px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium">
            🧮 Kira Loan Terus
          </span>
        </div>

        {/* Models Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
          {carModels.map((model) => (
            <ModelCard key={model.id} model={model} />
          ))}
        </div>

        {/* Bottom CTA Banner */}
        <div className="mt-10 md:mt-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 md:p-8 text-white">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-xl md:text-2xl font-bold mb-2">Tak Pasti Model Mana Sesuai?</h3>
              <p className="text-blue-100 text-sm md:text-base">
                WhatsApp saya dan saya akan bantu pilih model terbaik untuk anda!
              </p>
            </div>
            <Button
              onClick={() => window.open(`${advisorInfo.whatsappLink}?text=${encodeURIComponent('[Website] PM PERODUA - Hi Syasyah, saya nak tanya model mana yang sesuai untuk saya.')}`, '_blank')}
              className="bg-white text-blue-600 hover:bg-blue-50 font-bold px-6 md:px-8 py-3 md:py-4 rounded-xl text-sm md:text-base transition-all duration-300 whitespace-nowrap"
            >
              💬 WhatsApp Syasyah
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ModelsSection;
